import React, { useState, useEffect } from 'react';
import { Loader2, Download } from 'lucide-react';
import { citationService } from '@/services/CitationService';
import Modal from '@/components/Modal';
import { Button } from '@/components/ui/button';
import { PdfButtonData } from '@/components/MarkdownRenderer';
import './PdfPreviewModal.css';

interface PdfPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPdfData?: PdfButtonData | null;
}

const PdfPreviewModal: React.FC<PdfPreviewModalProps> = ({
  isOpen,
  onClose,
  selectedPdfData
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && selectedPdfData) {
      fetchPdf();
    }
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
    // eslint-disable-next-line
  }, [isOpen, selectedPdfData]);

  const fetchPdf = async () => {
    setIsLoading(true);
    setPdfUrl(null);
    try {
      const blob = await citationService.fetchCitation(selectedPdfData!.parsedData);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch {
      setPdfUrl(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = selectedPdfData?.textContent || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`PDF Preview: ${selectedPdfData?.textContent || ''}`}
      size="xl"
      footer={
        <div className="pdf-modal-footer">
          <Button onClick={onClose} variant="outline">Close</Button>
          {pdfUrl && (
            <Button onClick={handleDownload} variant="outline">
              <Download className="mr-2" size={16} />
              Download
            </Button>
          )}
        </div>
      }
      closeOnOverlayClick={false}
    >
      <div className="pdf-preview-modal">
        {isLoading ? (
          <div className="pdf-loading-state">
            <Loader2 className="loading-spinner" size={48} />
            <p className="loading-text">Loading PDF...</p>
          </div>
        ) : pdfUrl ? (
          <embed
            src={pdfUrl + '#toolbar=0&navpanes=0'}
            type="application/pdf"
            width="100%"
            height="700px"
            style={{ border: 'none', borderRadius: '0.5rem', backgroundColor: 'white' }}
          />
        ) : (
          <div className="pdf-empty-state">
            <p className="empty-message">Unable to load PDF.</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PdfPreviewModal;
